import {
    Box,
    Stack,
    Table,
    TableBody,
    TableContainer,
    useTheme
} from '@mui/material';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSortLabel
} from '@/components/shared/styles/Table';
import { useTranslation } from 'react-i18next';
import CardHeader from '@/components/shared/CardHeader';
import { TableCard } from '@/components/shared/Card';
import Chip from '@/components/shared/Chip';
import { SMBold, XSMedium } from '@/typography/Typography';
import { useFormatting } from '@/utils/formatting';
import { UserContext } from '@/contexts/user/UserContext';
import { useContext } from 'react';
import { TipsTransaction } from '@/services/PaymentsServiceV2';
import { useOrdering } from '@/lib/hooks/useOrdering';

interface TipsReconciliationTableProps {
    label: string;
    items: TipsTransaction[];
    totalAmount?: string;
    chipText?: string;
}

export const TipsReconciliationTable = ({
    label,
    items,
    chipText,
    totalAmount
}: TipsReconciliationTableProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { formatDate, formatDateTime, getBalanceLabel } = useFormatting();

    const { company } = useContext(UserContext);

    const { orderedItems, handleRequestSort, orderMap } = useOrdering(
        items || [],
        'date',
        'desc'
    );

    return (
        <TableCard>
            <CardHeader
                label={
                    <Stack direction={'row'} alignItems={'center'}>
                        {label}{' '}
                        <Box pl={1}>
                            <Chip
                                icon={<span></span>}
                                label={
                                    <XSMedium
                                        color={theme.palette.success.main}
                                    >
                                        {chipText}
                                    </XSMedium>
                                }
                            ></Chip>
                        </Box>
                    </Stack>
                }
            ></CardHeader>
            <TableContainer>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell width={'15%'}>
                                <TableSortLabel
                                    onClick={() => handleRequestSort('date')}
                                    active={orderMap['date'] !== undefined}
                                    direction={orderMap['date'] || 'asc'}
                                >
                                    {t('earnedAt')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell width={'15%'}>
                                <TableSortLabel
                                    onClick={() =>
                                        handleRequestSort('createdAt')
                                    }
                                    active={orderMap['createdAt'] !== undefined}
                                    direction={orderMap['createdAt'] || 'asc'}
                                >
                                    {t('distributedAt')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell width={'15%'}>
                                <TableSortLabel
                                    onClick={() =>
                                        handleRequestSort('reportedAt')
                                    }
                                    active={
                                        orderMap['reportedAt'] !== undefined
                                    }
                                    direction={orderMap['reportedAt'] || 'asc'}
                                >
                                    {t('reportedAt')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell width={'30%'}>
                                {t('description')}
                            </TableHeaderCell>
                            <TableHeaderCell width={'12.5%'} align="right">
                                {t('amount')}
                            </TableHeaderCell>
                            <TableHeaderCell width={'12.5%'} align="right">
                                {t('Sum')}
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orderedItems.map((item, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableCell>
                                        {formatDate(item.date, undefined, true)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(item.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(item.reportedAt)}
                                    </TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell align="right">
                                        {getBalanceLabel(
                                            item.amount,
                                            company.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {getBalanceLabel(
                                            item.sum,
                                            company.currency
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableFooterCell>
                                <SMBold>{t('total')}</SMBold>
                            </TableFooterCell>
                            <TableFooterCell></TableFooterCell>
                            <TableFooterCell></TableFooterCell>
                            <TableFooterCell></TableFooterCell>
                            <TableFooterCell align="right">
                                <SMBold>{totalAmount}</SMBold>
                            </TableFooterCell>
                            <TableFooterCell align="right">
                                <SMBold>{totalAmount}</SMBold>
                            </TableFooterCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </TableCard>
    );
};
